import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useMouseParallax } from '../hooks/useParallax.tsx';

interface ParallaxSpacerProps {
  height?: string;
  variant?: 'dots' | 'lines' | 'grid' | 'waves' | 'minimal';
  intensity?: number;
}

/**
 * Decorative spacer with parallax floating elements
 * Creates visual depth between sections
 */
export function ParallaxSpacer({ 
  height = '40vh', 
  variant = 'dots',
  intensity = 1 
}: ParallaxSpacerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { x: mouseX } = useMouseParallax(0.015 * intensity);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Different scroll speeds for parallax layers
  const y1 = useSpring(useTransform(scrollYProgress, [0, 1], [50 * intensity, -50 * intensity]), {
    stiffness: 100,
    damping: 30,
  });
  const y2 = useSpring(useTransform(scrollYProgress, [0, 1], [100 * intensity, -100 * intensity]), {
    stiffness: 100,
    damping: 30,
  });
  const y3 = useSpring(useTransform(scrollYProgress, [0, 1], [30 * intensity, -30 * intensity]), {
    stiffness: 100,
    damping: 30,
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <div 
      ref={ref}
      className="relative overflow-hidden pointer-events-none"
      style={{ height }}
      aria-hidden="true"
    >
      <motion.div className="absolute inset-0" style={{ opacity }}>
        {variant === 'dots' && (
          <DotsPattern y1={y1} y2={y2} y3={y3} mouseX={mouseX} />
        )}
        {variant === 'lines' && (
          <LinesPattern y1={y1} y2={y2} mouseX={mouseX} />
        )}
        {variant === 'grid' && (
          <GridPattern y1={y1} y2={y2} mouseX={mouseX} />
        )}
        {variant === 'waves' && (
          <WavesPattern y1={y1} y2={y2} scrollProgress={scrollYProgress} />
        )}
        {variant === 'minimal' && (
          <MinimalPattern y1={y1} y2={y2} y3={y3} mouseX={mouseX} />
        )}
      </motion.div>
    </div>
  );
}

// Floating dots pattern
function DotsPattern({ y1, y2, y3, mouseX }: any) {
  const dots = [
    // Layer 1 - Slow, large
    { x: '15%', y: '20%', size: 6, opacity: 0.15, layer: y1, mouseMultiplier: 0.5 },
    { x: '75%', y: '30%', size: 8, opacity: 0.1, layer: y1, mouseMultiplier: 0.4 },
    { x: '45%', y: '70%', size: 5, opacity: 0.12, layer: y1, mouseMultiplier: 0.6 },
    // Layer 2 - Medium speed
    { x: '25%', y: '50%', size: 4, opacity: 0.2, layer: y2, mouseMultiplier: 0.8 },
    { x: '85%', y: '60%', size: 3, opacity: 0.15, layer: y2, mouseMultiplier: 0.7 },
    { x: '55%', y: '25%', size: 5, opacity: 0.18, layer: y2, mouseMultiplier: 0.9 },
    { x: '10%', y: '80%', size: 4, opacity: 0.12, layer: y2, mouseMultiplier: 0.75 },
    // Layer 3 - Fast, small
    { x: '35%', y: '40%', size: 2, opacity: 0.25, layer: y3, mouseMultiplier: 1.2 },
    { x: '65%', y: '85%', size: 3, opacity: 0.2, layer: y3, mouseMultiplier: 1.1 },
    { x: '90%', y: '15%', size: 2, opacity: 0.22, layer: y3, mouseMultiplier: 1.3 },
  ];

  return (
    <>
      {dots.map((dot, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: dot.x,
            top: dot.y,
            width: dot.size,
            height: dot.size,
            opacity: dot.opacity,
            y: dot.layer,
            x: mouseX ? mouseX.get() * dot.mouseMultiplier : 0,
          }}
        />
      ))}
    </>
  );
}

// Floating lines pattern
function LinesPattern({ y1, y2, mouseX }: any) {
  const lines = [
    { x: '20%', y: '30%', width: 60, rotation: 45, opacity: 0.08, layer: y1 },
    { x: '70%', y: '50%', width: 80, rotation: -30, opacity: 0.06, layer: y1 },
    { x: '40%', y: '70%', width: 50, rotation: 15, opacity: 0.1, layer: y2 },
    { x: '85%', y: '20%', width: 40, rotation: -60, opacity: 0.07, layer: y2 },
    { x: '10%', y: '60%', width: 70, rotation: 30, opacity: 0.05, layer: y1 },
  ];

  return (
    <>
      {lines.map((line, i) => (
        <motion.div
          key={i}
          className="absolute h-px bg-gradient-to-r from-transparent via-white to-transparent"
          style={{
            left: line.x,
            top: line.y,
            width: line.width,
            opacity: line.opacity,
            rotate: line.rotation,
            y: line.layer,
            x: mouseX ? mouseX.get() * 0.5 : 0,
          }}
        />
      ))}
    </>
  );
}

// Subtle grid pattern
function GridPattern({ y1, y2, mouseX }: any) {
  return (
    <motion.div 
      className="absolute inset-0"
      style={{
        y: y1,
        x: mouseX ? mouseX.get() * 0.3 : 0,
      }}
    >
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      {/* Accent points at intersections */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full bg-white/20"
          style={{
            left: `${15 + i * 15}%`,
            top: `${20 + (i % 3) * 25}%`,
            y: y2,
          }}
        />
      ))}
    </motion.div>
  );
}

// Flowing waves pattern
function WavesPattern({ y1, y2, scrollProgress }: any) {
  const waveOffset = useTransform(scrollProgress, [0, 1], [0, 100]);
  
  return (
    <motion.div 
      className="absolute inset-0"
      style={{ y: y1 }}
    >
      {/* Wave 1 */}
      <motion.svg
        className="absolute w-full h-32 top-1/4"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
        style={{ x: waveOffset }}
      >
        <path
          d="M0,60 C300,120 600,0 900,60 C1050,90 1150,30 1200,60 L1200,120 L0,120 Z"
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="1"
        />
      </motion.svg>
      
      {/* Wave 2 */}
      <motion.svg
        className="absolute w-full h-32 top-1/2"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
        style={{ 
          x: useTransform(waveOffset, v => -v * 0.7),
          y: y2
        }}
      >
        <path
          d="M0,60 C200,0 400,120 600,60 C800,0 1000,120 1200,60"
          fill="none"
          stroke="rgba(255,255,255,0.03)"
          strokeWidth="1"
        />
      </motion.svg>
    </motion.div>
  );
}

// Minimal floating shapes
function MinimalPattern({ y1, y2, y3, mouseX }: any) {
  const shapes = [
    { type: 'circle', x: '20%', y: '30%', size: 100, layer: y1, mouseMultiplier: 0.3 },
    { type: 'square', x: '75%', y: '60%', size: 60, layer: y2, mouseMultiplier: 0.5 },
    { type: 'circle', x: '60%', y: '20%', size: 40, layer: y3, mouseMultiplier: 0.7 },
  ];

  return (
    <>
      {shapes.map((shape, i) => (
        <motion.div
          key={i}
          className={`absolute border border-white/5 ${
            shape.type === 'circle' ? 'rounded-full' : 'rounded-lg'
          }`}
          style={{
            left: shape.x,
            top: shape.y,
            width: shape.size,
            height: shape.size,
            y: shape.layer,
            x: mouseX ? mouseX.get() * shape.mouseMultiplier : 0,
            rotate: shape.type === 'square' ? 45 : 0,
          }}
        />
      ))}
    </>
  );
}

/**
 * Simpler decorative divider with parallax
 */
export function ParallaxDivider({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const { x: mouseX } = useMouseParallax(0.01);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  
  const width = useTransform(scrollYProgress, [0, 0.5, 1], ['0%', '100%', '0%']);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  return (
    <div ref={ref} className={`relative h-px my-8 ${className}`}>
      <motion.div
        className="absolute inset-y-0 left-1/2 -translate-x-1/2 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
        style={{ 
          width, 
          opacity,
          x: mouseX,
        }}
      />
    </div>
  );
}
